import * as _ from 'underscore';

import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { ListResult } from '../../ts/fetchers/ListResult';
import {
  GetSiteUsersRequest,
  IGetSiteUsersParams,
  IGetSiteUsersResult
} from '../../ts/ServerApi/requests/GetSiteUsersRequest';
import {
  GetUsersRequest,
  IGetUsersResult
} from '../../ts/ServerApi/requests/GetUsersRequest';
import { IGetItemsParams } from '../../ts/ServerApi/types/IGetItemsParams';
import { ISiteUser } from '../../ts/ServerApi/types/ISiteUser';
import { ITypedIds } from '../../ts/ServerApi/types/ITypedIds';
import {
  IUser,
  UserKeys
} from '../../ts/ServerApi/types/IUser';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { PermissionsHelper } from '../utils/PermissionsHelper';
import { PermissionsMerger } from '../utils/PermissionsMerger';
import { IGetEffectivePermissionsResult } from './types/GetEffectivePermissionsRequest';

class EffectivePermissionsService {

  private usersFetcher = new WebClientListResourceFetcher(GetUsersRequest, (result: IGetUsersResult) => result.users);
  private siteUsersFetcher = new WebClientListResourceFetcher(GetSiteUsersRequest, (result: IGetSiteUsersResult) => result.users);

  public getEffectivePermissions(
    usersParams: IGetItemsParams,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    return this.usersFetcher.fetchList(usersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<IUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      });
  }

  /**
   * Must be requested by a Site or Server admin
   */
  public getEffectivePermissionsWithSiteRole(
    siteUsersParams: IGetSiteUsersParams,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    return this.siteUsersFetcher.fetchList(siteUsersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<ISiteUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      });
  }

  public getEffectivePermissionsWithoutGettingSiteUsers(
    userIds: string[],
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    return this.getEffectivePermissionsForUserIds(userIds, authorizables, 0, userIds.length);
  }

  private getEffectivePermissionsInternal(
    users: ListResult<IUser> | ListResult<ISiteUser>,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    if (users.items.length === 0) {
      // no users; return empty permission list
      return new Promise((resolve) => {
        resolve(new ListResult<IGranteePermissions>([]));
      });
    } else {
      const userIds = _.pluck(users.items, UserKeys.id);
      return this.getEffectivePermissionsForUserIds(userIds, authorizables, users.index, users.total, users);
    }
  }

  private getEffectivePermissionsForUserIds(userIds: any[], authorizables: ITypedIds, startIndex: number, total: number, users?: ListResult<IUser> | ListResult<ISiteUser>) {
    const requests = PermissionsHelper.effectivePermissionsRequestsForAuthorizables(userIds, authorizables);
    const combineResultsFn = (results: IGetEffectivePermissionsResult[]) => {
      return {
        permissions: ResultHelper.combine(results, (result) => result.permissions),
        errors: ResultHelper.combineErrors(results)
      };
    };

    return WebHttpClient.sendRequestsParallel(requests, combineResultsFn)
      .then<ListResult<IGranteePermissions>>((result: IGetEffectivePermissionsResult) => {
        const list = PermissionsMerger.makeGranteePermissionsList(
          result.permissions,
          authorizables.ids,
          [], // groups is empty because getEffectivePermissions only supports users
          users?.items ?? userIds.map(userId => { return { id: userId } as IUser; })
        );
        return new ListResult<IGranteePermissions>(list, startIndex, total, result.errors);
      });
  }
}

export const EffectivePermissions = new EffectivePermissionsService();
